/**
 * @typedef {Object} VariantInventory
 * @property {number} id
 * @property {number} inventory_quantity
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.componentInitialized = false;
    this.addEventListener("change", this.onVariantChange);
    this.productId = this.dataset.productId;
    this.extendedOfferEnabled = this.dataset.extendedOfferEnabled;

    // switches
    this.shipsByBanner = {
      hide: () => {
        /** @type {HTMLElement | null} */
        const shipsByNotice = document.querySelector("#product-shipsby-notice");
        if (shipsByNotice) {
          shipsByNotice.style.display = "none";
        }
      },
      show: () => {
        /** @type {HTMLElement | null} */
        const shipsByNotice = document.querySelector("#product-shipsby-notice");
        if (shipsByNotice) {
          shipsByNotice.style.display = "flex";
        }
      },
    };

    // Add initialization of buyButtonText here
    this.buyButtonText = {
      addToCart: "Add to Cart",
      soldOut: "Sold Out",
      extendedOffer: "Extended Offer",
      clickToBuy: "Click to Buy",
      unavailable: "Unavailable",
      mandatoryCustomization: "Add to cart",
    };
  }

  connectedCallback() {
    // Update with dataset values if provided
    this.buyButtonText = {
      addToCart: this.dataset.textAddToCart || this.buyButtonText.addToCart,
      soldOut: this.dataset.textSoldout || this.buyButtonText.soldOut,
      extendedOffer:
        this.dataset.textExtendedOffer || this.buyButtonText.extendedOffer,
      clickToBuy: this.dataset.textClickToBuy || this.buyButtonText.clickToBuy,
      unavailable:
        this.dataset.textUnavailable || this.buyButtonText.unavailable,
      mandatoryCustomization:
        this.dataset.textMandatoryCustomization ||
        this.buyButtonText.mandatoryCustomization,
    };

    this.customizable = this.dataset.customizable === "true";
    // TO DO: Make dynamic via metafield
    this.customizationMandatory =
      this.dataset.customizationMandatory === "true";

    /**
     * @type {HTMLScriptElement | null}
     */
    const productElement = this.querySelector("script[id='product-json']");
    if (!productElement) {
      console.error("Could not find product json element");
      return;
    }
    try {
      this.product = JSON.parse(productElement.innerHTML);
    } catch (e) {
      console.error("Failed to parse product JSON:", e);
      return;
    }

    // Hidden variants plumbing (used only when product has a single option)
    /** @type {Set<string>} */
    this.hiddenVariantIdSet = new Set();
    const hiddenEl = this.querySelector("script[id='hidden-variant-ids']");
    if (hiddenEl && hiddenEl.innerHTML) {
      try {
        const arr = JSON.parse(hiddenEl.innerHTML);
        if (Array.isArray(arr)) {
          this.hiddenVariantIdSet = new Set(arr.map((id) => id.toString()));
        } else {
          console.warn("hidden-variant-ids JSON is not an array:", arr);
        }
      } catch (e) {
        console.error("Failed to parse hidden-variant-ids JSON:", e);
      }
    }

    this.productType = this.dataset.productType;
    this.isGiftCard = this.productType === "Gift Card";

    const inventoryElement = this.querySelector(
      "script[id='variant-inventory']"
    );
    if (!inventoryElement) {
      console.error("Could not find variant inventory element");
      return;
    }
    try {
      this.variantInventory = JSON.parse(inventoryElement.innerHTML);
    } catch (e) {
      console.error("Failed to parse variant-inventory JSON:", e);
      return;
    }
    // console.log("variant-inventory: ", this.variantInventory);

    /**
     * @type {HTMLElement | null}
     * */
    this.section = this.closest("section");
    if (!this.section) {
      console.error("Could not find section element for variant-selects");
      return;
    }
    this.sectionId = this.section.classList[0].replace("product-section--", "");
    /**
     * @type {HTMLInputElement | null}
     * */
    this.skuElement = this.section.querySelector("input[name='sku']");

    /**
     * @type {HTMLButtonElement | null}
     * */
    this.addToCartButton = this.section.querySelector(
      `#product-form-${this.sectionId} .product-form__submit`
    );

    /**
     * @type {HTMLButtonElement | null}
     * */
    this.extendedOfferButton = this.section.querySelector(
      `#extended-offer-button-${this.sectionId}`
    );

    const firstAvailableVariant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) => variant.available
    );

    messenger.listen(messenger.topics.RESET_PRODUCT_FORM, () => {
      const firstAvailableVariant = this.getFirstAvailableVariant();
      this.initUi();
    });

    window.addEventListener("message", (event) => {
      if (
        event.data.type === "ships_by_notification_ready" &&
        event.data.productId === this.product.id.toString() &&
        !this.componentInitialized
      ) {
        this.initUi();
      }
    });

    messenger.listen(messenger.topics.STICKY_ADD_TO_CART_READY, () => {
      if (!this.componentInitialized) {
        this.initUi.bind(this);
      }
    });

    this.holidayShippingEnable =
      this.dataset.holidayShippingEnable === "true" ? true : false;
    if (this.holidayShippingEnable && !this.componentInitialized) {
      this.initUi();
    }
  }

  getFirstAvailableVariant() {
    let candidateVariants = this.product.variants;
    if (
      this.product &&
      this.product.options &&
      this.product.options.length === 1 &&
      this.hiddenVariantIdSet
    ) {
      candidateVariants = this.product.variants.filter(
        /** @param {Variant} v */ (v) =>
          !this.hiddenVariantIdSet.has(v.id.toString())
      );
    }

    // Prefer inventory > 0 using variantInventory map
    const hasInventory = /** @param {Variant} variant */ (variant) => {
      const entry = this.variantInventory.find(
        /** @param {any} inv */ (inv) => inv.id === variant.id
      );
      return entry && entry.inventory_quantity > 0;
    };

    return (
      candidateVariants.find(hasInventory) ||
      candidateVariants.find(
        /** @param {Variant} variant */ (variant) => variant.available
      ) ||
      candidateVariants[0]
    );
  }

  // handlers

  onVariantChange() {
    // console.trace("onVariantChange");
    this.updateCurrentVariant();
    this.updateURL();
    this.updateVariantInput();
    this.renderVariantPrice();
    this.setCta();
    this.postMessageVariantChanged();
  }

  setCta() {
    if (!this.currentVariant) {
      this.setUnavailableUI();
    } else if (this.customizable && this.customizationMandatory) {
      this.setMandatoryCustomizableUI();
    } else if (
      (this.currentVariant && this.currentVariant.inventoryQuantity > 0) ||
      this.isGiftCard
    ) {
      this.setAddToCartUI();
    } else if (this.extendedOfferEnabled) {
      this.setExtendedOfferUI();
    } else {
      this.setSoldOutUI();
    }
  }

  /**
   * @param {boolean} show
   * @param {boolean} [withOverlap=false] - Optional parameter to add overlap class
   */
  toggleSoldOutElement(show, withOverlap = false) {
    if (!this.section) {
      console.error("Could not find section element for variant-selects");
      return;
    }
    /** @type {HTMLElement | null} */
    const soldOutElement = this.section.querySelector(".product__sold-out");
    if (!soldOutElement) return;

    soldOutElement.style.display = show ? "flex" : "none";

    if (
      show &&
      withOverlap &&
      this.section.querySelector("ships-by-notification")
    ) {
      soldOutElement.classList.add("overlap");
    } else {
      soldOutElement.classList.remove("overlap");
    }
  }

  setAddToCartUI() {
    // console.log("setAddToCartUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.toggleExtendedOfferButton(false);
    this.setShipsByVisibility(true);
    this.addToCartButton.textContent = this.buyButtonText.addToCart;
    this.addToCartButton.removeAttribute("disabled");
    this.addToCartButton.type = "submit";
    this.setHolidayShippingVisibility(true);

    this.toggleSoldOutElement(false);

    messenger.emmit(messenger.topics.PP_SETTING_UI, { uiState: "add-to-cart" });
  }

  setExtendedOfferUI() {
    // console.log("setExtendedOfferUI");
    this.toggleExtendedOfferButton(true);
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(false);

    this.toggleSoldOutElement(true, true);

    messenger.emmit(messenger.topics.PP_SETTING_UI, {
      uiState: "extended-offer",
    });
  }

  setSoldOutUI() {
    // console.log("setSoldOutUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(false);
    this.addToCartButton.textContent = this.buyButtonText.soldOut;
    this.addToCartButton.removeAttribute("disabled");

    this.toggleSoldOutElement(true, true);

    messenger.emmit(messenger.topics.PP_SETTING_UI, { uiState: "sold-out" });
  }

  setUnavailableUI() {
    // console.log("setUnavailableUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.toggleExtendedOfferButton(false);
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(false);
    this.addToCartButton.textContent = this.buyButtonText.unavailable;
    this.addToCartButton.removeAttribute("disabled");
    this.addToCartButton.type = "button";

    this.toggleSoldOutElement(false, true);
  }

  setMandatoryCustomizableUI() {
    // console.log("setMandatoryCustomizableUI");
    if (!this.addToCartButton) {
      console.error("Could not find add to cart button");
      return;
    }
    this.toggleExtendedOfferButton(false);
    this.setHolidayShippingVisibility(false);
    this.setShipsByVisibility(true);
    this.addToCartButton.textContent =
      this.buyButtonText.mandatoryCustomization;
    this.addToCartButton.removeAttribute("disabled");
    this.addToCartButton.type = "button";

    this.toggleSoldOutElement(false, true);
  }

  /** @param {boolean} visible */
  setShipsByVisibility(visible) {
    /** @type {Array<HTMLElement> | null} */
    const shipsByElementsArray = Array.from(
      document.querySelectorAll("ships-by-notification")
    );
    if (!shipsByElementsArray || shipsByElementsArray.length === 0) return;
    const shipsByElements = shipsByElementsArray.filter((item) => {
      const section = item.getAttribute("data-section");
      return section && section.includes("main");
    });
    if (!shipsByElements) return;
    if (visible) {
      shipsByElements.forEach((element) => {
        element.style.display = "block";
      });
    } else {
      shipsByElements.forEach((element) => {
        element.style.display = "none";
      });
    }
  }

  /** @param {boolean} visible */
  setHolidayShippingVisibility(visible) {
    /** @type {HTMLElement | null} */
    const holidayShippingElement = document.querySelector(
      "holiday-shipping-countdown"
    );
    if (!holidayShippingElement) return;
    if (visible && this.holidayShippingEnable) {
      holidayShippingElement.style.display = "block";
    } else {
      holidayShippingElement.style.display = "none";
    }
  }

  initUi() {
    // console.trace("initUI called");
    let variant =
      this.getVariantFromUrl() ||
      this.getFirstAvailableVariant() ||
      this.product.variants[0];

    // If URL variant exists but is OOS, prefer first available
    if (variant) {
      if (!variant) return;
      const inv = this.variantInventory.find(
        /** @param {any} i */ (i) => i.id === variant.id
      );
      if (inv && inv.inventory_quantity <= 0) {
        variant = this.getFirstAvailableVariant() || variant;
      }
    }

    if (!variant) {
      // No visible variants (likely all hidden) => set Unavailable UI
      this.setUnavailableUI();
      return;
    }

    // Filter dropdown values if single-option product
    this.filterSingleOptionDropdown();
    this.setVariantSelectsToValues(variant.options);

    this.setShipsBy(this.product.tags);

    const event = new Event("change", {
      bubbles: true,
    });

    this.dispatchEvent(event);
    this.componentInitialized = true;
  }

  /**
   *
   * @param {string[]} tags
   */
  setShipsBy(tags) {
    let /** @type {string} */ tagShipsBy;
    if (!tags || tags.length === 0) {
      console.log("No tags provided, using default ships by");
      tagShipsBy = defaultShippingDate;
      console.log("Tags provided:", tags);
    } else {
      tagShipsBy = knocking.getTagShippingDate(tags);
    }
    const shipsByString = knocking.calculateAbsoluteShippingDate(tagShipsBy);
    console.log("shipsByString: ", shipsByString);
    this.postMessageShipsBy(shipsByString);
    /**
     * @type {HTMLInputElement | null}
     * */
    let shipsByInput;
    /**
     * @type {HTMLElement | null}
     * */
    const section = this.closest("section");

    shipsByInput = section ? section.querySelector("#ships-by-input") : null;
    if (shipsByInput) {
      shipsByInput.value = tagShipsBy;
    }
  }

  /**
   *
   * @param {string[]} options
   */
  setVariantSelectsToValues(options) {
    const selects = Array.from(this.querySelectorAll("select"));

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      selects[i].value = option;
    }
  }

  // Filter dropdown values for single-option products to exclude hidden variants
  filterSingleOptionDropdown() {
    if (
      !this.product ||
      !this.product.options ||
      this.product.options.length !== 1
    ) {
      return;
    }
    const select = this.querySelector("select");
    if (!select) return;

    // Visible (non-hidden) variants
    const visible = this.product.variants.filter(
      /** @param {Variant} v */ (v) =>
        !this.hiddenVariantIdSet.has(v.id.toString())
    );

    // Split by inventory availability
    const withInvValues = [];
    const withoutInvValues = [];
    for (const v of visible) {
      const inv = this.variantInventory.find((i) => i.id === v.id);
      const qty = inv ? inv.inventory_quantity : 0;
      const val = v.options[0];
      if (qty > 0) {
        withInvValues.push(val);
      } else {
        withoutInvValues.push(val);
      }
    }

    // Deduplicate, prioritize in-stock values first
    const uniq = /** @param {string[]} arr */ (arr) => Array.from(new Set(arr));
    const inStock = uniq(withInvValues);
    const outOfStock = uniq(
      withoutInvValues.filter((v) => !inStock.includes(v))
    );
    const values = [...inStock, ...outOfStock];

    // Rebuild options in the desired order
    select.innerHTML = values
      .map((val) => `<option value=\"${val}\">${val}</option>`)
      .join("");

    if (values.length === 0) {
      this.setUnavailableUI();
      return;
    }

    // Initialize to first available (in-stock) value if present, else first visible
    select.value = values[0];
  }

  postMessageVariantChanged() {
    if (!this.currentVariant) {
      return;
    }
    // When variant is changed post a message with the variant's data
    messenger.emmit(messenger.topics.VARIANT_CHANGE, this.currentVariant);
  }

  /**
   *
   * @param {string} shippingDate
   */
  postMessageShipsBy(shippingDate) {
    window.postMessage({
      type: "update_ships_by_notification",
      productId: this.productId,
      shippingDate: shippingDate,
    });
  }

  /**
   *
   * @param {Boolean} show
   */
  toggleExtendedOfferButton(show) {
    // show extended offer button if exists
    if (this.extendedOfferButton) {
      this.extendedOfferButton.style.display = show ? "flex" : "none";
    }
    if (this.addToCartButton) {
      this.addToCartButton.style.display = show ? "none" : "flex";
    }
  }

  renderVariantPrice() {
    if (!this.currentVariant) return;

    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) {
          price.classList.remove("visibility-hidden");
        }
      });
  }

  // update
  getVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");
    if (!variantId) return null;
    const variant = this.product.variants.find(
      /** @param {Variant} variant */ (variant) =>
        variant.id.toString() == variantId
    );
    if (!variant) return null;
    // If single-option and variant is hidden, ignore URL
    if (
      this.product &&
      this.product.options &&
      this.product.options.length === 1 &&
      this.hiddenVariantIdSet &&
      this.hiddenVariantIdSet.has(variant.id.toString())
    ) {
      return null;
    }
    return variant;
  }

  /**
   *
   * @param {Variant | null} variant
   * @returns
   */
  updateCurrentVariant(variant = null) {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
    const options = this.options;
    if (!options) {
      console.error("Can not determine variant-options");
      return;
    }
    if (variant) {
      this.currentVariant = variant;
    } else {
      const variantFound = this.product.variants.find(
        /** @param {Variant} variant */ (variant) => {
          return !variant.options
            .map((option, index) => {
              return options[index] === option;
            })
            .includes(false);
        }
      );

      if (!variantFound) {
        console.error("Can not find variant");
        this.currentVariant = null;
        return;
      }
      // If single-option and this variant is hidden, treat as not found
      if (
        this.product &&
        this.product.options &&
        this.product.options.length === 1 &&
        this.hiddenVariantIdSet &&
        this.hiddenVariantIdSet.has(variantFound.id.toString())
      ) {
        this.currentVariant = null;
        return;
      }

      this.currentVariant = variantFound;

      const inventory = this.variantInventory.find(
        /** @param {VariantInventory} inventory */ (inventory) => {
          return inventory.id === this.currentVariant.id;
        }
      );
      this.currentVariant.inventoryQuantity = inventory.inventory_quantity;
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  /**
   * @param {Variant | null} variant
   */
  updateVariantInput(variant = null) {
    if (!variant && !this.currentVariant) {
      return;
    }

    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );

    productForms.forEach((productForm) => {
      /**
       * @type {HTMLInputElement | null}
       * */
      const input = productForm.querySelector('input[name="id"]');
      if (!input) {
        console.log("Could not find input element, aborting...");
        return;
      }

      /**
       * @type {HTMLInputElement | null}
       * */
      const skuElement = productForm.querySelector('input[name="sku"]');
      if (!skuElement) {
        console.log(
          "Could not find sku element, might not be rendered yet...?"
        );
      }

      if (variant) {
        input.value = variant.id.toString();
        if (skuElement) {
          skuElement.value = variant.sku;
        }
      } else {
        input.value = this.currentVariant.id;
        if (skuElement) {
          skuElement.value = this.currentVariant.sku;
        }
      }
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // not in use
  /**
   * @param {string} name
   * @param {any} value
   * @param {boolean} visible
   */
  appendProperty(name, value, visible = false) {
    let productForm = document.querySelector(
      'form[id^="product-form-template"]'
    );
    if (!productForm) {
      console.log("Can not find product form, aborting...");
      return;
    }

    let propertyInput = document.createElement("input");
    let nameProperty;
    if (visible) {
      nameProperty = `properties[${name}]`;
    } else {
      nameProperty = `properties[_${name}]`;
    }
    propertyInput.setAttribute("type", "hidden");
    propertyInput.setAttribute("name", nameProperty);
    propertyInput.setAttribute("value", value.toString());
    productForm.appendChild(propertyInput);
  }

  cleanUpPropertyInputs() {
    const productForm = document.querySelector(
      'form[id^="product-form-template"]'
    );
    if (!productForm) return;
    const inputs = Array.from(productForm.querySelectorAll("input"));
    const propertyInputs = inputs.filter((input) => {
      const name = input.getAttribute("name");
      if (name && name.includes("properties")) {
        return input;
      }
    });
    propertyInputs.forEach((input) => {
      input.remove();
    });
  }
}

customElements.define("variant-selects", VariantSelects);
